import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Input,
  InputAdornment,
  Stack,
  Text,
  useTheme,
} from '@ltvco/refresh-lib/theme';
import { updateAccountInfo } from '@ltvco/refresh-lib/utils';
import { useSnackbar } from '@ltvco/refresh-lib/v1';
import { useQueryClient } from '@ltvco/refresh-lib/vendors';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useState } from 'react';

interface UpdatePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const UpdatePasswordModal = ({
  isOpen,
  onClose,
  onSuccess,
}: UpdatePasswordModalProps) => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [logoutOtherSessions, setLogoutOtherSessions] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentPasswordError, setCurrentPasswordError] = useState<
    string | null
  >(null);
  const [newPasswordError, setNewPasswordError] = useState<string | null>(null);
  const [confirmPasswordError, setConfirmPasswordError] = useState<
    string | null
  >(null);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const resetForm = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setLogoutOtherSessions(false);
    setCurrentPasswordError(null);
    setNewPasswordError(null);
    setConfirmPasswordError(null);
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPasswordError(null);
    setNewPasswordError(null);
    setConfirmPasswordError(null);

    // Same validation rules as ChangePasswordModal (yup schema)
    if (!currentPassword.trim()) {
      setCurrentPasswordError('Current password is required');
      return;
    }

    if (!newPassword.trim()) {
      setNewPasswordError('New password is required');
      return;
    }

    if (newPassword.length < 8) {
      setNewPasswordError('Password must be at least 8 characters');
      return;
    }

    if (!confirmPassword.trim()) {
      setConfirmPasswordError('Please confirm your password');
      return;
    }

    if (newPassword !== confirmPassword) {
      setConfirmPasswordError('Passwords do not match');
      return;
    }

    setIsSubmitting(true);

    try {
      await updateAccountInfo({
        account: {
          password: newPassword,
          password_confirmation: confirmPassword,
          invalidate_sessions: logoutOtherSessions,
        },
        currentPassword,
      });

      await queryClient.invalidateQueries({ queryKey: ['account'] });
      enqueueSnackbar('Password updated successfully', { variant: 'success' });
      handleClose();
      onSuccess?.();
    } catch (err) {
      const apiMessage = err instanceof Error ? err.message : 'invalid';
      const lowerMessage = apiMessage.toLowerCase();

      // New password errors (reused, already used, etc.) -> show on New password field
      const isNewPasswordError =
        lowerMessage.includes('already used') ||
        lowerMessage.includes('used before') ||
        lowerMessage.includes('reuse') ||
        lowerMessage.includes('reused') ||
        lowerMessage.includes('previously used');

      if (isNewPasswordError) {
        setNewPasswordError(apiMessage);
      } else {
        // Current password wrong or other errors -> show on Current password field
        const displayMessage =
          lowerMessage.includes('wrong password') ||
          lowerMessage.includes('incorrect password')
            ? 'invalid'
            : apiMessage;
        setCurrentPasswordError(displayMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '8px',
          border: `2px solid ${theme.palette.primary.light}`,
        },
      }}
    >
      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ padding: '24px' }}>
          <Stack spacing={2.5}>
            <Text variant="h5" fontWeight={700} color="text.primary">
              Password
            </Text>

            <Input
              fullWidth
              type={showCurrentPassword ? 'text' : 'password'}
              placeholder="Current password"
              value={currentPassword}
              onChange={(e) => {
                setCurrentPassword(e.target.value);
                setCurrentPasswordError(null);
              }}
              required
              error={!!currentPasswordError}
              helperText={currentPasswordError}
              inputProps={{ 'data-cy': 'current_password' }}
              slotProps={{
                input: {
                  endAdornment: currentPassword ? (
                    <InputAdornment position="end" sx={{ marginRight: 1 }}>
                      <IconButton
                        onClick={() => setShowCurrentPassword((prev) => !prev)}
                        onMouseDown={(e) => e.preventDefault()}
                        edge="end"
                        aria-label={
                          showCurrentPassword
                            ? 'Hide password'
                            : 'Show password'
                        }
                      >
                        {showCurrentPassword ? (
                          <VisibilityOff fontSize="small" />
                        ) : (
                          <Visibility fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: theme.palette.background.paper,
                },
              }}
            />

            <Input
              fullWidth
              type={showNewPassword ? 'text' : 'password'}
              placeholder="New password"
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                setNewPasswordError(null);
              }}
              required
              error={!!newPasswordError}
              helperText={newPasswordError}
              inputProps={{ 'data-cy': 'new_password' }}
              slotProps={{
                input: {
                  endAdornment: newPassword ? (
                    <InputAdornment position="end" sx={{ marginRight: 1 }}>
                      <IconButton
                        onClick={() => setShowNewPassword((prev) => !prev)}
                        onMouseDown={(e) => e.preventDefault()}
                        edge="end"
                        aria-label={
                          showNewPassword ? 'Hide password' : 'Show password'
                        }
                      >
                        {showNewPassword ? (
                          <VisibilityOff fontSize="small" />
                        ) : (
                          <Visibility fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: theme.palette.background.paper,
                },
              }}
            />

            <Input
              fullWidth
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="Re-enter new password"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                setConfirmPasswordError(null);
              }}
              required
              error={!!confirmPasswordError}
              helperText={confirmPasswordError}
              inputProps={{ 'data-cy': 'confirm_password' }}
              slotProps={{
                input: {
                  endAdornment: confirmPassword ? (
                    <InputAdornment position="end" sx={{ marginRight: 1 }}>
                      <IconButton
                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                        onMouseDown={(e) => e.preventDefault()}
                        edge="end"
                        aria-label={
                          showConfirmPassword
                            ? 'Hide password'
                            : 'Show password'
                        }
                      >
                        {showConfirmPassword ? (
                          <VisibilityOff fontSize="small" />
                        ) : (
                          <Visibility fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: theme.palette.background.paper,
                },
              }}
            />
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            padding: '16px 24px 24px',
            gap: 1,
          }}
        >
          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={isSubmitting}
            data-cy="update_password_submit"
          >
            {isSubmitting ? 'Saving...' : 'Update'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
